import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BellRing } from 'lucide-react';
import { SavedWording } from './DropSavedWording';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const opts = { withCredentials: true };
const STATUS = {
  ok: null,
  push_off: 'Back-in-stock pushes are turned off in Settings, so you can only clear the list.',
  push_unconfigured: 'Push notifications are not set up, so you can only clear the list.',
  not_orderable: 'Still Sold out or hidden. Switch Sold out off first.',
  none_waiting: 'Nobody is waiting.',
};
const inputCls = 'w-full bg-[#050003] border border-[#2A0F3D] focus:border-[#39FF14] font-mono text-sm px-2 py-2 text-white outline-none';

// Editable back-in-stock push. Closing = decide later (waitlist kept).
export function RestockPushPreview({ target, onDone }) {
  const [pv, setPv] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!target) return;
    setPv(null);
    axios.get(`${API_URL}/api/products/${target.productId}/restock/preview`, { ...opts, params: { region: target.region } })
      .then(({ data }) => { setPv(data); setTitle(data.title); setBody(data.body); setPicked(null); })
      .catch(() => { toast.error('Could not load restock push'); onDone(false); });
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (send) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/products/${target.productId}/restock/push`,
        { region: target.region, send, title: title.trim(), body: body.trim() }, opts);
      toast.success(send ? `Restock push sent · reached ${data.pushed} of ${data.waiting} waiting` : `Waitlist cleared · ${data.cleared} not notified`);
      onDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onDone(false); }}>
      <DialogContent className="bg-[#0C0612] border-[#39FF14]/40 max-w-md" data-testid="restock-push-preview">
        <DialogHeader>
          <DialogTitle className="font-mono text-[#39FF14] flex items-center gap-2"><BellRing className="w-4 h-4" /> TELL WAITING CUSTOMERS?</DialogTitle>
          <DialogDescription className="font-mono text-[11px] text-[#6B8E73]">The product is orderable again. Close this to decide later; the waitlist is kept.</DialogDescription>
        </DialogHeader>
        {!pv ? <p className="font-mono text-xs text-[#6B8E73]">Loading…</p> : (
          <div className="space-y-3 font-mono text-xs">
            <p className="text-[#39FF14]" data-testid="restock-push-waiting">{pv.product_name} · {pv.region.toUpperCase()} · {pv.waiting} waiting</p>
            {STATUS[pv.status] && <p className="text-[#FF9F0A]" data-testid="restock-push-status">{STATUS[pv.status]}</p>}
            <SavedWording region={pv.region} productName={pv.product_name} initial={pv.saved_messages} endpoint={`${API_URL}/api/restock/saved`}
              title={title} body={body} activeId={picked && picked.title === title && picked.body === body ? picked.id : null}
              onPick={(m) => { setTitle(m.title); setBody(m.body); setPicked(m); }} />
            <label className="block space-y-1 text-[#A3C7A8] uppercase text-[10px]">Title · {title.length}/60
              <input value={title} maxLength={60} onChange={(e) => setTitle(e.target.value)} className={inputCls} data-testid="restock-push-title" />
            </label>
            <label className="block space-y-1 text-[#A3C7A8] uppercase text-[10px]">Message · {body.length}/180
              <textarea value={body} maxLength={180} rows={3} onChange={(e) => setBody(e.target.value)} className={`${inputCls} resize-none`} data-testid="restock-push-body" />
            </label>
            <div className="border border-[#2A0F3D] bg-[#050003] p-2" data-testid="restock-push-mock">
              <p className="text-white text-[12px] font-bold truncate">{title || '—'}</p>
              <p className="text-[#A3C7A8] text-[11px]">{body || '—'}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => submit(false)} disabled={busy || !pv.waiting || pv.status === 'not_orderable'}
                className="h-11 border border-[#6B8E73]/60 text-[#A3C7A8] uppercase tracking-wider hover:bg-white/5 disabled:opacity-40" data-testid="restock-push-skip">Don&apos;t notify</button>
              <button type="button" onClick={() => submit(true)} disabled={busy || pv.status !== 'ok' || !title.trim() || !body.trim()}
                className="h-11 bg-[#39FF14] text-[#050003] font-bold uppercase tracking-wider disabled:opacity-40" data-testid="restock-push-send">
                {busy ? 'Sending…' : 'Send push'}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
